import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import config from 'config';
import classNames from 'classnames';

import { VideoCtvRedux } from 'store-types';
import WithLoading from 'components/common/WithLoading';
import LoadingOverlay from 'components/common/LoadingOverlay';
import { usePlayerAction } from 'components/providers/PlayerStateContext';
import Player from 'support/player/Player';
import device from 'common/support/device';
import { useCheckNetworkInterval } from 'hooks/network';
import { clearVideoIsClicked } from 'actions/clickedVideo';

import PlayerDebugOverlay from './PlayerDebugOverlay';
import styles from './Player.module.scss';

interface PlayerComponentProps {
  resource?: unknown | null;
  onPlaylistComplete?: () => void;
  onResourceSet?: () => void;
  onError?: (error: unknown) => void;
  isCcEnabled?: boolean;
  loading?: boolean;
  id?: string | null;
  isCarousel?: boolean;
  fullScreenPlayer?: boolean;
  inFocus?: boolean;
}

type PlayerInstance = InstanceType<typeof Player> | null;

const PlayerState = {
  LOADING: 'LOADING',
  ACTIVE: 'ACTIVE', // Playing or Paused
  RESOURCE_BUFFERING: 'RESOURCE_BUFFERING',
  COMPLETED: 'COMPLETED',
  ERROR: 'ERROR',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseActiveCues = (activeCues: Record<string, any>) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cues: Record<string, any> = {};
  Object.keys(activeCues).forEach(key => {
    const cue = activeCues[key];
    cues[key] = {
      align: cue.align,
      line: cue.line,
      position: cue.position,
      size: cue.size,
      snapToLines: cue.snapToLines,
      text: cue.text,
      startTime: cue.startTime,
      endTime: cue.endTime,
      track: cue.track,
      vertical: cue.vertical,
    };
  });
  return cues;
};

const PlayerComponent = ({
  resource = null,
  onPlaylistComplete = () => null,
  onResourceSet = () => null,
  onError = () => null,
  isCcEnabled = false,
  loading = false,
  id = 'CVP',
  isCarousel = false,
  fullScreenPlayer = false,
}: PlayerComponentProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<PlayerInstance>(null);
  const [isReady, setReady] = useState(false);
  const [playerState, setPlayerState] = useState(PlayerState.LOADING);
  const dispatch = useDispatch();

  const isSeeking = useRef<boolean | null>(null);
  const {
    setPlayer,
    setIsPlaying,
    setContentStarted,
    setContentChanged,
    setPosition,
    setAdBreakStarted,
    setAdPosition,
    setAdBreakCompleted,
    setCues,
    resetResource,
    unmute,
  } = usePlayerAction()!;

  useCheckNetworkInterval();

  const latestOnError = useRef(onError);
  useEffect(() => {
    latestOnError.current = onError;
  });
  const latestOnResourceSet = useRef(onResourceSet);
  useEffect(() => {
    latestOnResourceSet.current = onResourceSet;
  });

  useEffect(() => {
    const player = playerRef.current;
    if (isReady && resource && player) {
      // TODO: Think about handling resource arrays for playlists.
      // Only plausible use-case for now would be client-injected pre-rolls
      // if (resource instanceof Array) {
      //   player.playlist.clear();
      //   player.playlist.addResources(resource);
      //   // TODO: Re-visit via CSVSTV-405
      //   // Some combination of CVP playlist clear() and addResources() does not behave predictably/synchronously with start().
      //   // Manually handle via timer
      //   const timer = setTimeout(() => {
      //     player.playlist.start();
      //     setContentChanged();
      //   }, 100);
      //   return () => clearTimeout(timer);
      // }

      player.setResource(resource);
      setContentChanged();
      latestOnResourceSet.current();
    }
    return undefined;
  }, [resource, isReady, setContentChanged]);

  const enableCaptions = useCallback(
    (enable: boolean) => {
      if (playerState === PlayerState.ACTIVE && playerRef.current) {
        playerRef.current.enableTextTrack(enable);
      }
    },
    [playerState],
  );
  useEffect(() => {
    enableCaptions(isCcEnabled);
  }, [isCcEnabled, enableCaptions, playerState]);

  const isNetworkConnected = useSelector((state: VideoCtvRedux) => state.network.networkConnected);

  const previouslyConnected = useSelector(
    (state: VideoCtvRedux) => state.network.previouslyConnected,
  );

  useEffect(() => {
    if (isNetworkConnected && !previouslyConnected) {
      resetResource();
    }
  }, [isNetworkConnected, previouslyConnected, resetResource]);

  useEffect(() => {
    const player = playerRef.current;
    if (fullScreenPlayer && player) {
      unmute();
    }
  }, [fullScreenPlayer, unmute]);

  const id3ParamKeys = [
    'ID3GameEndTransitions',
    'ID3StreamEndTransitions',
    'gameEnd',
    'broadcastEnd',
  ];

  const ks = useSelector((state: VideoCtvRedux) => state.kitchenSink.data);

  // Get relevant ID3 parameters from ks.config.modules
  const id3Params = Object.fromEntries(
    Object.entries(ks.config?.modules || {}).filter(([key]) => id3ParamKeys.includes(key)),
  );

  // Eventually want to move this to Player class.
  const onPlayerEvent = (...args: unknown[]) => {
    const { PlayerEvent } = Player;
    const [e] = args;
    const { type, detail } = e as {
      type: string;
      detail: Record<string, unknown> & { cue?: { id?: string; text?: string } };
      fatal?: boolean;
    };

    if (config.playback.debug) {
      // eslint-disable-next-line no-console
      console.log('[PlayerEvent]', e);
    }

    switch (type) {
      case PlayerEvent.QUALITIES_CHANGE:
      case PlayerEvent.QUALITY_CHANGE:
        setPlayerState(PlayerState.RESOURCE_BUFFERING);
        break;
      case PlayerEvent.RESOURCE_START:
        (fullScreenPlayer || !isCarousel) && unmute();
        setPlayerState(PlayerState.LOADING);
        setCues();
        break;
      case PlayerEvent.RESOURCE_BUFFERING:
        setPlayerState(detail.value ? PlayerState.LOADING : PlayerState.RESOURCE_BUFFERING);
        break;
      case PlayerEvent.CONTENT_START:
        setContentStarted();
        setPlayerState(PlayerState.ACTIVE);
        break;
      case PlayerEvent.CONTENT_PLAYING:
        setIsPlaying(true);
        setPlayerState(PlayerState.ACTIVE);
        device.setScreenSaver(false);
        break;
      case PlayerEvent.CONTENT_PAUSED:
        setIsPlaying(false);
        device.setScreenSaver(true);
        break;
      case PlayerEvent.CONTENT_PROGRESS:
        if (!isSeeking.current) {
          setPosition(detail.streamTime as number, detail.streamDuration as number);
          setPlayerState(PlayerState.ACTIVE);
        }
        break;
      case PlayerEvent.SEEK_START:
        setPlayerState(PlayerState.LOADING);
        isSeeking.current = true;
        break;
      case PlayerEvent.SEEK_COMPLETE:
        setPlayerState(PlayerState.ACTIVE);
        isSeeking.current = false;
        break;
      case PlayerEvent.AD_BREAK_START:
        setAdBreakStarted();
        setPlayerState(PlayerState.ACTIVE);
        break;
      case PlayerEvent.AD_START:
        setCues();
        break;
      case PlayerEvent.AD_PROGRESS:
        setAdPosition(detail.currentTime as number, detail.duration as number);
        break;
      case PlayerEvent.AD_BREAK_COMPLETE:
        setAdBreakCompleted();
        break;
      case PlayerEvent.CONTENT_COMPLETE:
        setPlayerState(PlayerState.COMPLETED);
        onPlaylistComplete();
        device.setScreenSaver(true);
        break;
      case PlayerEvent.ERROR: {
        if (detail.error !== undefined && detail.error !== null) {
          const { fatal } = (detail.error ?? {}) as { fatal?: boolean };

          if (fatal) {
            latestOnError.current(e);
            setPlayerState(PlayerState.ERROR);
            device.setScreenSaver(true);
          }
        }

        break;
      }
      case PlayerEvent.TEXT_CUEPOINT: {
        const { activeCues } = detail as { activeCues: Record<string, unknown> };
        setCues(parseActiveCues(activeCues));
        break;
      }

      case PlayerEvent.METADATA_CUEPOINT: {
        const cueId = detail.cue?.id;
        const cueText = detail.cue?.text;

        if (!cueId || !cueText || !id3Params) break;

        const broadcastEndEvents = id3Params?.broadcastEnd?.params?.[cueId];
        const gameEndEvents = id3Params?.gameEnd?.params?.[cueId];

        const matchesCue = (events: string | string[]) =>
          Array.isArray(events) ? events.includes(cueText) : events === cueText;

        if (broadcastEndEvents && matchesCue(broadcastEndEvents)) {
          setPlayerState(PlayerState.COMPLETED);
          onPlaylistComplete();
        } else if (gameEndEvents && matchesCue(gameEndEvents)) {
          setPlayerState(PlayerState.COMPLETED);
          onPlaylistComplete();
        }
        break;
      }
      default:
        break;
    }
  };

  const playerEventRef = useRef<((...args: unknown[]) => void) | null>(null);
  useEffect(() => {
    playerEventRef.current = onPlayerEvent;
  });

  useEffect(() => {
    let player = playerRef.current;
    if (resource && !player) {
      const handlePlayerEvent = (...params: unknown[]) => {
        return playerEventRef.current?.(...params);
      };

      const initializePlayer = async () => {
        try {
          // 1. Create the video player
          player = new Player(handlePlayerEvent);
          await player.initializePlayer(containerRef.current ?? null, id ?? undefined);

          playerRef.current = player;
          device.setPlayerRef(player);
          setPlayer(player);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error((e as Error).message);
        }
      };

      initializePlayer()
        .then(() => {
          setReady(true);
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.error('A problem occured during player creation ', e);
        });
    }
  }, [setPlayer, resource, id]);

  useEffect(() => {
    return () => {
      const player = playerRef.current;
      if (player) {
        dispatch(clearVideoIsClicked());
        player.destroy();
      }
    };
  }, [dispatch]);

  const getLoadingState = () => {
    if (isCarousel && !fullScreenPlayer) return false;
    return loading || [PlayerState.LOADING, PlayerState.RESOURCE_BUFFERING].includes(playerState);
  };

  const hidePlayer =
    isCarousel &&
    playerState !== PlayerState.ACTIVE &&
    playerState !== PlayerState.RESOURCE_BUFFERING;

  return (
    <WithLoading
      loading={getLoadingState()}
      renderLoading={() => <LoadingOverlay className={styles.loading} />}
      enableTts={false}
    >
      <div
        id={`playerContainer-${id}`}
        className={classNames(hidePlayer ? styles.hidePlayer : undefined, styles.playerContainer)}
        ref={containerRef}
      />
      {config.playback.debug && <PlayerDebugOverlay />}
    </WithLoading>
  );
};

export default PlayerComponent;
